// src/services/assetMaintenanceService.js
// Service module for Asset Maintenance related API calls
// UPDATED: Added Update/Delete functions, robust error handling, and header management.

// Import shared API helpers
import { authenticatedFetch } from './api';

const BASE_ENDPOINT = '/maintenance';

/**
 * Fetches maintenance history for a specific asset.
 * @param {string|number} assetId - The ID of the asset.
 * @returns {Promise<Array>} A promise that resolves to an array of maintenance history objects.
 * @throws {Error} If API call fails or auth token is missing, or if data format is unexpected.
 */
export const fetchAssetMaintenanceHistory = async (assetId) => {
  if (!assetId) {
    console.warn("[AssetMaintenanceService] fetchAssetMaintenanceHistory called without assetId.");
    return []; // Return empty array if no asset ID is provided
  }

  // Adjust endpoint based on your backend route (query param vs path param)
  const endpoint = `${BASE_ENDPOINT}?assetId=${assetId}`;
  console.log(`[AssetMaintenanceService] Fetching maintenance history for asset ${assetId} from: ${endpoint}`);

  try {
    // Use the shared authenticated fetch helper
    const responseData = await authenticatedFetch(endpoint);

    // Ensure data is an array (Handle { data: [...] } or [...])
    const history = Array.isArray(responseData) ? responseData : (responseData?.data || []);

    if (!Array.isArray(history)) {
      console.error(`[AssetMaintenanceService] fetchAssetMaintenanceHistory received non-array data for asset ${assetId}:`, history);
      throw new Error("Received invalid data format for maintenance history from server.");
    }

    console.log(`[AssetMaintenanceService] Fetched ${history.length} maintenance records for asset ${assetId}.`);
    return history;
  } catch (error) {
    // Re-throw any errors from authenticatedFetch or validation/parsing
    console.error(`[AssetMaintenanceService] Error fetching maintenance history for asset ${assetId}:`, error);
    throw error; 
  }
};

/**
 * Creates a new asset maintenance record.
 * @param {object} maintenanceData - Data for the new maintenance record.
 * @returns {Promise<object>} The newly created maintenance record object from the backend response.
 * @throws {Error} If API call fails or auth token is missing.
 */
export const createAssetMaintenance = async (maintenanceData) => {
  console.log("[AssetMaintenanceService] Creating asset maintenance record", maintenanceData);

  const options = {
    method: 'POST',
    // Robustness: Explicitly stringify body and set Content-Type
    body: JSON.stringify(maintenanceData),
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const result = await authenticatedFetch(BASE_ENDPOINT, options);
    // Assuming backend returns the created maintenance object directly or nested
    return result?.maintenance || result?.data || result;
  } catch (error) {
    console.error("[AssetMaintenanceService] Error creating record:", error);
    throw error;
  }
};

/**
 * Alias for createAssetMaintenance — used by integration layers
 * that import `createMaintenanceLog`.
 */
export const createMaintenanceLog = createAssetMaintenance;

/**
 * Updates an existing asset maintenance record.
 * @param {string|number} id - The ID of the maintenance record to update.
 * @param {object} updateData - The fields to update.
 * @returns {Promise<object>} The updated record.
 */
export const updateAssetMaintenance = async (id, updateData) => {
  if (!id) {
    throw new Error("[AssetMaintenanceService] Update failed: Missing maintenance record ID.");
  }

  const endpoint = `${BASE_ENDPOINT}/${id}`;
  console.log(`[AssetMaintenanceService] Updating record ${id}`);

  const options = {
    method: 'PUT',
    body: JSON.stringify(updateData),
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const result = await authenticatedFetch(endpoint, options);
    return result?.maintenance || result?.data || result;
  } catch (error) {
    console.error(`[AssetMaintenanceService] Error updating record ${id}:`, error);
    throw error;
  }
};

/**
 * Deletes an asset maintenance record.
 * @param {string|number} id - The ID of the maintenance record to delete.
 * @returns {Promise<boolean>} True if successful.
 */
export const deleteAssetMaintenance = async (id) => {
  if (!id) {
    throw new Error("[AssetMaintenanceService] Delete failed: Missing maintenance record ID.");
  }

  const endpoint = `${BASE_ENDPOINT}/${id}`;
  console.log(`[AssetMaintenanceService] Deleting record ${id}`);

  const options = {
    method: 'DELETE',
  };

  try {
    await authenticatedFetch(endpoint, options);
    return true;
  } catch (error) {
    console.error(`[AssetMaintenanceService] Error deleting record ${id}:`, error);
    throw error;
  }
};

// Default export object for convenience
const assetMaintenanceService = {
  fetchAssetMaintenanceHistory,
  createAssetMaintenance,
  createMaintenanceLog,
  updateAssetMaintenance,
  deleteAssetMaintenance
};

export default assetMaintenanceService;